// <s-scent-quiz> — step-by-step scent finder for templates/page.quiz.liquid.
// Shows one [data-step] at a time, records each [data-answer] button (its data-w adds
// scent-family weights), picks the [data-profile] with the highest family score, and ranks
// the server-rendered [data-match] perfumes by how closely their note profile (top, heart
// and base, base weighted most) matches the answers. Answers persist in sessionStorage so the native email form can reload
// the page and land straight on the results.

const STORAGE_KEY = 'ps-scent-quiz';

const FAMILIES = {
  fresh: ['bergamot', 'lemon', 'orange', 'citrus', 'grapefruit', 'mandarin', 'marine', 'sea', 'aquatic', 'mint', 'lavender', 'apple', 'pear', 'melon', 'neroli', 'lime', 'ginger', 'green'],
  clean: ['musk', 'iris', 'powder', 'aldehyde', 'cotton', 'soap', 'ambrofix', 'ambroxan', 'white tea', 'lily'],
  woody: ['cedar', 'sandalwood', 'vetiver', 'wood', 'oakmoss', 'cypress', 'pine', 'patchouli', 'guaiac', 'birch', 'moss'],
  gourmand: ['vanilla', 'tonka', 'caramel', 'chocolate', 'cocoa', 'coffee', 'honey', 'praline', 'almond', 'sugar', 'toffee', 'marshmallow'],
  spicy: ['cinnamon', 'pepper', 'cardamom', 'saffron', 'nutmeg', 'clove', 'spice', 'anise'],
  oriental: ['oud', 'amber', 'leather', 'incense', 'tobacco', 'benzoin', 'labdanum', 'resin', 'myrrh', 'smoke'],
  floral: ['rose', 'jasmine', 'tuberose', 'blossom', 'peony', 'geranium', 'hibiscus', 'violet', 'floral', 'magnolia', 'ylang', 'freesia'],
  fruity: ['raspberry', 'blackcurrant', 'peach', 'cherry', 'berry', 'plum', 'pineapple', 'fruit', 'lychee', 'mango', 'apricot'],
};

// Multi-word notes resolved before single words, so "orange blossom" is floral (not citrus) and "pineapple" is fruity (not pine).
const PHRASES = {
  'orange blossom': ['floral'], 'orange flower': ['floral'], 'lemon blossom': ['floral'], 'pineapple': ['fruity'],
  'rosewood': ['woody'], 'agarwood': ['oriental', 'woody'], 'pink pepper': ['spicy', 'fresh'], 'black pepper': ['spicy'],
  'tonka bean': ['gourmand'], 'white musk': ['clean'], 'sea salt': ['fresh'], 'green notes': ['fresh'],
  'ylang-ylang': ['floral'], 'lily of the valley': ['floral', 'clean'], 'soft woods': ['woody'], 'cashmere wood': ['woody', 'clean'],
};
FAMILIES.clean.push('orris', 'cashmere');
FAMILIES.oriental.push('agar', 'ambergris');
FAMILIES.floral.push('heliotrope', 'osmanthus', 'lily', 'orchid');
FAMILIES.woody.push('cashmeran', 'akigalawood', 'oak');
// Notes found in the ProScentss catalogue that no family covered (Oct 2026 audit).
FAMILIES.fresh.push('sage', 'clary sage', 'juniper', 'elemi');
FAMILIES.floral.push('chamomile', 'carnation', 'hedione');
FAMILIES.oriental.push('frankincense', 'cognac');
FAMILIES.gourmand.push('chestnut', 'hazelnut', 'truffle', 'cognac');
FAMILIES.fruity.push('strawberry', 'passionfruit', 'passion fruit', 'berries');
FAMILIES.spicy.push('elemi', 'carnation');
const TIER_WEIGHTS = [0.6, 1, 1.4];

// Matches family words only at the start of a word, so "grapefruit" is not "fruit" and "pineapple" is not "apple".
function familiesForNote(note) {
  const lower = note.toLowerCase();
  const phrase = Object.keys(PHRASES).find((p) => lower.includes(p));
  if (phrase) return PHRASES[phrase];
  return Object.entries(FAMILIES)
    .filter(([, words]) => words.some((w) => new RegExp(`(^|[^a-z])${w}`).test(lower)))
    .map(([family]) => family);
}

const PROFILE_FAMILIES = {
  bold: ['oriental', 'spicy'],
  warm: ['gourmand'],
  fresh: ['fresh', 'clean'],
  woody: ['woody'],
  floral: ['floral', 'fruity'],
};

class SScentQuiz extends HTMLElement {
  #ac = null;
  #steps = [];
  #history = [];
  #state = { answers: {}, labels: {}, weights: {} };

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#steps = [...this.querySelectorAll('[data-step]')];

    this.addEventListener('click', (event) => {
      const answer = event.target.closest('[data-answer]');
      if (answer && this.contains(answer)) return this.#choose(answer);
      if (event.target.closest('[data-next]')) return this.#next();
      if (event.target.closest('[data-quiz-back]')) return this.#back();
      if (event.target.closest('[data-quiz-restart]')) return this.#restart();
    }, opts);

    const form = this.querySelector('#scent-quiz-email');
    form?.addEventListener('submit', () => {
      const profile = this.#profile();
      const tags = this.querySelector('[data-quiz-tags]');
      if (tags) tags.value = `newsletter,scent-quiz,quiz-${profile}`;
      this.#save({ submitted: true });
    }, opts);

    const saved = this.#load();
    const posted = this.querySelector('[data-quiz-posted]') || new URLSearchParams(location.search).has('customer_posted');
    if (saved && saved.submitted && posted) {
      this.#state = saved;
      const dialog = this.closest('dialog');
      if (dialog && !dialog.open) dialog.showModal();
      this.#show('results');
    } else {
      this.#show(this.#steps[0]?.dataset.step);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #choose(button) {
    const key = button.dataset.answer;
    const section = button.closest('[data-step]');
    section.querySelectorAll('[data-answer]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    this.#state.answers[key] = button.dataset.value;
    this.#state.labels[key] = [...button.querySelectorAll('span')].map((s) => s.textContent.trim()).find(Boolean) || '';
    this.#state.weights[key] = button.dataset.w || '';
    this.#save();
    this.#next();
  }

  #next() {
    const index = this.#currentIndex();
    const nextStep = this.#steps[index + 1];
    if (!nextStep) return;
    this.#history.push(this.#steps[index].dataset.step);
    this.#show(nextStep.dataset.step);
  }

  #back() {
    const previous = this.#history.pop();
    if (previous) this.#show(previous);
  }

  #restart() {
    this.#state = { answers: {}, labels: {}, weights: {} };
    this.#history = [];
    sessionStorage.removeItem(STORAGE_KEY);
    this.querySelectorAll('[aria-pressed]').forEach((b) => b.removeAttribute('aria-pressed'));
    this.#show(this.#steps[0].dataset.step);
  }

  #currentIndex() {
    return this.#steps.findIndex((step) => !step.hidden);
  }

  #show(name) {
    const index = this.#steps.findIndex((step) => step.dataset.step === name);
    if (index < 0) return;
    this.#steps.forEach((step, i) => { step.hidden = i !== index; });

    const isResults = name === 'results';
    const total = this.#steps.length - 1;
    const bar = this.querySelector('[data-quiz-bar]');
    const count = this.querySelector('[data-quiz-count]');
    const progress = this.querySelector('[data-quiz-progress]');
    if (progress) progress.hidden = isResults;
    if (bar) bar.style.width = `${Math.round(((index + 1) / total) * 100)}%`;
    if (count) count.textContent = (this.dataset.stepOf || '').replace('[current]', index + 1).replace('[total]', total);

    const back = this.querySelector('[data-quiz-back]');
    if (back) back.hidden = index === 0 || isResults;

    if (isResults) this.#renderResults();

    const heading = this.#steps[index].querySelector('h2');
    if (heading && this.#history.length) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
    if (this.#history.length || isResults) this.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  #familyScores() {
    const scores = Object.fromEntries(Object.keys(FAMILIES).map((f) => [f, 0]));
    Object.values(this.#state.weights).forEach((spec) => {
      String(spec || '').split(',').forEach((pair) => {
        const [family, value] = pair.split(':');
        if (family && family in scores) scores[family] += Number(value) || 0;
      });
    });
    return scores;
  }

  #profile() {
    const scores = this.#familyScores();
    let best = 'woody';
    let bestScore = -1;
    Object.entries(PROFILE_FAMILIES).forEach(([profile, families]) => {
      const score = families.reduce((sum, f) => sum + scores[f], 0);
      if (score > bestScore) { best = profile; bestScore = score; }
    });
    return best;
  }

  #renderResults() {
    const profile = this.#profile();
    this.querySelectorAll('[data-profile]').forEach((el) => { el.hidden = el.dataset.profile !== profile; });

    const labels = this.#state.labels;
    const told = this.querySelector('[data-told-us-text]');
    if (told && labels.frustration && labels.weekend && labels.mood) {
      told.textContent = (this.dataset.toldUs || '')
        .replace('[frustration]', labels.frustration)
        .replace('[weekend]', labels.weekend)
        .replace('[mood]', labels.mood);
      told.hidden = false;
    }

    const scores = this.#familyScores();
    const topFamilies = Object.entries(scores).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([f]) => f);
    const shopperNorm = Math.sqrt(Object.values(scores).reduce((sum, v) => sum + v * v, 0)) || 1;
    const gender = this.#state.answers.gender || 'unisex';
    const allowed = gender === 'unisex' ? ['unisex'] : [gender, 'unisex'];

    const seen = new Set();
    const ranked = [];
    this.querySelectorAll('[data-match]').forEach((item) => {
      item.hidden = true;
      if (!allowed.includes(item.dataset.gender) || seen.has(item.dataset.handle)) return;
      seen.add(item.dataset.handle);
      // Build the perfume's family profile: base notes weigh most (they are what is worn all day), top notes least.
      const tiers = (item.dataset.notes || '').split('|');
      const profile = Object.fromEntries(Object.keys(FAMILIES).map((f) => [f, 0]));
      const noteFamilies = [];
      tiers.forEach((tier, index) => {
        const weight = TIER_WEIGHTS[Math.min(index, TIER_WEIGHTS.length - 1)];
        tier.split(',').map((n) => n.trim()).filter(Boolean).forEach((note) => {
          const families = familiesForNote(note);
          families.forEach((family) => { profile[family] += weight / families.length; });
          if (families.length) noteFamilies.push({ note, families, weight });
        });
      });
      // Cosine similarity: rewards perfumes whose overall character matches the answers, regardless of how many notes they list, and penalises unchosen families.
      const perfumeNorm = Math.sqrt(Object.values(profile).reduce((sum, v) => sum + v * v, 0));
      const dot = Object.keys(profile).reduce((sum, f) => sum + profile[f] * scores[f], 0);
      let score = perfumeNorm ? dot / (perfumeNorm * shopperNorm) : 0;
      const matched = noteFamilies
        .filter(({ families }) => families.some((f) => topFamilies.includes(f)))
        .sort((a, b) => b.weight - a.weight)
        .map(({ note }) => note)
        .filter((note, i, all) => all.indexOf(note) === i);
      if (item.dataset.available !== 'true') score -= 1000;
      ranked.push({ item, score, matched });
    });

    ranked.sort((a, b) => b.score - a.score);
    const list = this.querySelector('[data-quiz-matches]');
    const top = ranked.slice(0, 3);
    top.forEach(({ item, matched }) => {
      const reason = item.querySelector('[data-match-reason-text]');
      if (reason) {
        reason.textContent = matched.length
          ? (this.dataset.matchReason || '').replace('[notes]', matched.slice(0, 2).join(' + '))
          : '';
      }
      list?.append(item);
      item.hidden = false;
    });
    const empty = this.querySelector('[data-quiz-empty]');
    if (empty) empty.hidden = top.length > 0;
  }

  #save(extra = {}) {
    try {
      this.#state = { ...this.#state, ...extra };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.#state));
    } catch (_) { /* storage unavailable: quiz still works in-page */ }
  }

  #load() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch (_) {
      return null;
    }
  }
}

if (!customElements.get('s-scent-quiz')) customElements.define('s-scent-quiz', SScentQuiz);
